import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Swords } from 'lucide-react';

const ROUND_LABELS = {
  final: 'גמר', semi_final: 'חצי גמר', quarter_final: 'רבע גמר',
  round_of_16: 'שמינית גמר', round_of_32: '1/32', group_stage: 'שלב בתים',
  group_a: 'בית א', group_b: 'בית ב', group_c: 'בית ג', group_d: 'בית ד',
  consolation_final: 'גמר תנחומים', third_place: 'מקום שלישי',
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
};

const HeadToHeadCard = ({ meId, meName = 'אתה', opponentId, opponentName = 'היריב' }) => {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meId || !opponentId) return;
    setLoading(true);
    axios
      .get(`${API}/players/${meId}/vs/${opponentId}`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API, meId, opponentId]);

  if (loading || !data) return null;
  if (data.total_meetings === 0) {
    return (
      <Card data-testid="head-to-head-card" dir="rtl">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Swords className="w-6 h-6 text-rose-500" />
            מאזן מפגשים מולך
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-slate-500 py-6">עדיין לא נפגשתם על המגרש. שיהיה בהצלחה במשחק הראשון! 🎾</p>
        </CardContent>
      </Card>
    );
  }

  const { player_wins: mine, opponent_wins: theirs, history } = data;
  const total = mine + theirs;
  const minePct = total ? Math.round((mine / total) * 100) : 50;

  return (
    <Card data-testid="head-to-head-card" dir="rtl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <Swords className="w-6 h-6 text-rose-500" />
          מאזן מפגשים מולך
          <span className="text-sm font-normal text-slate-400">· {data.total_meetings} מפגשים</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Balance header */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-center flex-1">
            <div className="text-3xl font-black text-emerald-600" data-testid="h2h-my-wins">{mine}</div>
            <div className="text-xs text-slate-500 truncate">{meName}</div>
          </div>
          <div className="text-slate-300 font-bold text-xl">—</div>
          <div className="text-center flex-1">
            <div className="text-3xl font-black text-rose-500" data-testid="h2h-opp-wins">{theirs}</div>
            <div className="text-xs text-slate-500 truncate">{opponentName}</div>
          </div>
        </div>
        <div className="h-2.5 w-full rounded-full bg-rose-200 overflow-hidden mb-1">
          <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${minePct}%` }} />
        </div>
        <p className="text-center text-sm font-semibold text-slate-600 mb-5">
          {mine > theirs ? `אתה מוביל ${mine}-${theirs} 💪` : mine < theirs ? `${opponentName} מוביל ${theirs}-${mine}` : `תיקו ${mine}-${theirs} 🤝`}
        </p>

        {/* Matches list */}
        <div className="space-y-2">
          {history.map((m) => {
            const win = m.result === 'win';
            return (
              <div
                key={m.match_id}
                data-testid={`h2h-match-${m.match_id}`}
                className={`flex items-center gap-3 rounded-xl border-2 p-3 ${win ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`}
              >
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${win ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
                  {win ? 'ניצחון' : 'הפסד'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">
                    {m.tournament_name || 'תחרות'} · {ROUND_LABELS[m.round] || m.round}
                  </div>
                  <div className="text-xs text-slate-400">{fmtDate(m.date)}</div>
                </div>
                <div className="flex gap-1">
                  {m.sets.map((s, i) => (
                    <span key={i} className="text-sm font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5">
                      {s.you}-{s.opp}
                    </span>
                  ))}
                  {m.win_type === 'walkover' && <span className="text-xs text-amber-600 font-bold">W.O.</span>}
                  {m.win_type === 'retirement' && <span className="text-xs text-orange-600 font-bold">פרישה</span>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default HeadToHeadCard;
